/* eslint-disable @typescript-eslint/dot-notation */
import { useRouter } from 'next/router';
import { FC, useState } from 'react';

import { getCurrentPlatformLink } from '@bpm-web-app/utils';
import classNames from 'classnames';
import Image from 'next/image';
import { SubmitHandler, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import styles from './gift.module.css';
import { Account, AccountPageProps } from '../account';
import { PageHeader } from '../components/page-header/page-header';
import { Button } from '../components/button/button';
import Gift from '../../../assets/images/Gift.png';
import { Input } from '../components/form-fields/input/input';

type Props = AccountPageProps;

export const GiftPage: FC<Props> = ({ title }) => {
    const router = useRouter();

    const {
        register,
        setValue,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues:
        {
            selectedPresetAmount: '35.00',
            customAmount: ''
        },

        resolver: joiResolver(
            Joi.object({
                selectedPresetAmount: Joi.number().allow(null),
                customAmount: Joi.any().messages({
                    'number.base': 'Please enter a valid amount.',
                    'number.min': 'The minimum amount is $35.00.',
                    'any.required': 'Please enter an amount.'
                }).when('selectedPresetAmount', {
                    is: Joi.valid(null),
                    then: Joi.number().min(35).required(),
                    otherwise: Joi.optional()
                })
            })
        ),
    });

    const selectedPresetAmount = watch('selectedPresetAmount');

    const submitForm: SubmitHandler<{ customAmount: string; selectedPresetAmount: string | null }> = async (data) => {
        // If preset is selected, use it; otherwise use custom amount
        const finalAmount = data.selectedPresetAmount || data.customAmount;

        // Validate that we have an amount
        if (!finalAmount) {
            return;
        }

        // If using custom amount, validate it meets minimum
        if (!data.selectedPresetAmount) {
            const numAmount = parseFloat(data.customAmount);
            if (Number.isNaN(numAmount) || numAmount < 35) {
                return;
            }
        }

        router.push({
            pathname: getCurrentPlatformLink('/account/gifts/buy'),
            query: { amount: finalAmount },
        });
    };

    return (
        <Account title="Gift Card" extraWide>
            <PageHeader title="" />
            <div className={styles['gift-container']}>
                <div className={styles['gift-image']}>
                    <div className={styles['image-wrapper']}>
                        <Image src={Gift} alt="Gift" className={styles['image']} />
                    </div>
                </div>
                <form onSubmit={handleSubmit(submitForm)}>
                    <div className={styles['gift-details']}>
                        <div className={styles['gift-header']}>
                            <div>
                                <h1 className={styles['gift-title']}>BPM Supreme E-Gift Card</h1>
                                <p className={styles['gift-subtitle']}>
                                    Delivered by Email
                                </p>
                            </div>
                            <div className={styles['gift-description']}>
                                Give them access to the music DJs rely on. DJ-ready tracks, exclusive edits, and the versions they need to start mixing or build their catalog.
                                Our suggested gift amounts match the costs of our most popular plans, so your recipient can easily choose the subscription that fits them.
                                They can use their credit toward any plan and any library of music they want.
                            </div>

                        </div>
                        <div className={styles['amount-section']}>
                            <div className={styles['amount-selectors']}>
                                <Button
                                    variant="inverted"
                                    as="button"
                                    type="button"
                                    className={classNames(styles['amount-option'], {
                                        [styles['selected']]: selectedPresetAmount === '35.00'
                                    })}
                                    onClick={() => {
                                        setValue('selectedPresetAmount', '35.00');
                                        setValue('customAmount', '');
                                    }}>$35.00
                                </Button>
                                <Button
                                    variant="inverted"
                                    as="button"
                                    type="button"
                                    className={classNames(styles['amount-option'], {
                                        [styles['selected']]: selectedPresetAmount === '70.00'
                                    })}
                                    onClick={() => {
                                        setValue('selectedPresetAmount', '70.00');
                                        setValue('customAmount', '');
                                    }}>$70.00
                                </Button>
                                <Button
                                    variant="inverted"
                                    as="button"
                                    type="button"
                                    className={classNames(styles['amount-option'], {
                                        [styles['selected']]: selectedPresetAmount === '350.00'
                                    })}
                                    onClick={() => {
                                        setValue('selectedPresetAmount', '350.00');
                                        setValue('customAmount', '');
                                    }}>$350.00
                                </Button>
                            </div>

                            <div className={styles['or-divider']}>or</div>

                            <div className={styles['custom-amount']}>
                                <Input
                                    label="Custom (min. $35.00)"
                                    type="number"
                                    step={0.01}
                                    {...register('customAmount', {
                                        onChange: () => {
                                            setValue('selectedPresetAmount', null as any);
                                        }
                                    })}
                                    error={errors.customAmount?.message}
                                />
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            as="button"
                            type="submit"
                            disabled={false}
                            className={classNames(styles['buy-button'])}
                        >Checkout
                        </Button>
                    </div>
                </form>
            </div>
        </Account>
    );
};
/* eslint-enable @typescript-eslint/dot-notation */
